import React, { useState, useCallback, useEffect } from 'react';
import {
  Upload, FileText, X, Check, AlertCircle,
  ArrowLeft, ArrowRight, Trash2, File, Download
} from 'lucide-react';
import { pdfAPI } from '../../services/api';
import './PDFStagingModal.css';

const PDFStagingModal = ({
  tender,
  existingPdfs,
  uploadedPdfs,
  onUpload,
  onSkip,
  onContinue,
  onBack
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(true);
  const [existingPdfList, setExistingPdfList] = useState(existingPdfs || []);
  const [localUploads, setLocalUploads] = useState(uploadedPdfs || []);
  const [error, setError] = useState(null);

  // Fetch existing PDFs for this tender
  useEffect(() => {
    const fetchExistingPdfs = async () => {
      if (!tender) return;

      try {
        setLoadingExisting(true);

        // First, use discovered_pdfs from the tender object if available
        const discoveredPdfs = tender.discovered_pdfs || [];
        let allPdfs = discoveredPdfs.map(pdf => ({
          name: pdf.title || pdf.url?.split('/').pop() || 'Document',
          url: pdf.url,
          type: 'discovered',
          source: 'elasticsearch'
        }));

        // Also try API call for any additional indexed PDFs
        try {
          const tenderId = tender.tender_id || tender.id || tender.reference_number;
          const response = await pdfAPI.getTenderPDFs(tenderId);
          const apiPdfs = (response.pdfs || response.documents || []).map(pdf => ({
            ...pdf,
            type: 'indexed',
            source: 'api'
          }));

          // Merge, avoiding duplicates by URL
          const existingUrls = new Set(allPdfs.map(p => p.url));
          apiPdfs.forEach(pdf => {
            if (!existingUrls.has(pdf.url)) {
              allPdfs.push(pdf);
            }
          });
        } catch (apiErr) {
          console.log('API fetch failed, using discovered PDFs only:', apiErr.message);
        }

        console.log(`Found ${allPdfs.length} existing PDFs for tender`);
        setExistingPdfList(allPdfs);
      } catch (err) {
        console.error('Error fetching existing PDFs:', err);
        // Not critical - we can proceed without existing PDFs
      } finally {
        setLoadingExisting(false);
      }
    };

    fetchExistingPdfs();
  }, [tender]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    setError(null);

    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  }, []);

  const handleFileInput = (e) => {
    setError(null);
    const files = Array.from(e.target.files);
    handleFiles(files);
  };

  const handleFiles = (files) => {
    const validFiles = files.filter(file => {
      const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
      const isValidSize = file.size <= 50 * 1024 * 1024; // 50MB limit

      if (!isPdf) {
        setError('Only PDF files are accepted');
        return false;
      }
      if (!isValidSize) {
        setError('File size must be under 50MB');
        return false;
      }
      return true;
    });

    if (validFiles.length > 0) {
      const newFiles = validFiles.map(file => ({
        id: `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        name: file.name,
        size: file.size,
        file: file,
        status: 'pending'
      }));

      setLocalUploads(prev => [...prev, ...newFiles]);
      onUpload(newFiles);
    }
  };

  const removeUpload = (fileId) => {
    setLocalUploads(prev => prev.filter(f => f.id !== fileId));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const totalPdfs = existingPdfList.length + localUploads.length;

  return (
    <div className="pdf-staging-modal">
      <div className="staging-header">
        <div className="header-icon">
          <Upload size={32} />
        </div>
        <div className="header-text">
          <h3>Stage Additional Documents</h3>
          <p>Upload any additional RFP documents for more accurate analysis</p>
        </div>
      </div>

      {/* Existing PDFs Section */}
      <div className="pdfs-section">
        <h4>
          <FileText size={18} />
          Existing Tender Documents
          {loadingExisting && <span className="loading-text">Loading...</span>}
        </h4>

        {!loadingExisting && existingPdfList.length === 0 ? (
          <div className="empty-pdfs">
            <AlertCircle size={20} />
            <span>No existing documents found for this tender</span>
          </div>
        ) : (
          <div className="pdf-list">
            {existingPdfList.map((pdf, index) => (
              <div key={index} className="pdf-item existing">
                <FileText size={18} className="pdf-icon" />
                <div className="pdf-info">
                  <span className="pdf-name">{pdf.filename || pdf.name || `Document ${index + 1}`}</span>
                  {pdf.size && <span className="pdf-size">{formatFileSize(pdf.size)}</span>}
                </div>
                <Check size={16} className="status-icon existing" title="Already indexed" />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upload Section */}
      <div className="upload-section">
        <h4>
          <Upload size={18} />
          Upload Additional Documents
        </h4>

        <div
          className={`dropzone ${dragActive ? 'active' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <input
            type="file"
            id="pdf-upload"
            accept=".pdf,application/pdf"
            multiple
            onChange={handleFileInput}
            className="file-input"
          />
          <label htmlFor="pdf-upload" className="dropzone-content">
            <Upload size={40} className="upload-icon" />
            <span className="dropzone-text">
              Drag & drop PDF files here, or <span className="browse-link">browse</span>
            </span>
            <span className="dropzone-hint">
              Accepted: PDF files up to 50MB each
            </span>
          </label>
        </div>

        {error && (
          <div className="upload-error">
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        {localUploads.length > 0 && (
          <div className="uploaded-list">
            <h5>Uploaded Files ({localUploads.length})</h5>
            {localUploads.map((file) => (
              <div key={file.id} className="pdf-item uploaded">
                <File size={18} className="pdf-icon" />
                <div className="pdf-info">
                  <span className="pdf-name">{file.name}</span>
                  <span className="pdf-size">{formatFileSize(file.size)}</span>
                </div>
                <button
                  className="remove-btn"
                  onClick={() => removeUpload(file.id)}
                  title="Remove file"
                >
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Summary */}
      <div className="staging-summary">
        <div className="summary-stat">
          <span className="stat-value">{totalPdfs}</span>
          <span className="stat-label">Total Documents</span>
        </div>
        <div className="summary-stat">
          <span className="stat-value">{existingPdfList.length}</span>
          <span className="stat-label">Existing</span>
        </div>
        <div className="summary-stat">
          <span className="stat-value">{localUploads.length}</span>
          <span className="stat-label">New Uploads</span>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="action-buttons">
        <button className="btn-secondary" onClick={onBack}>
          <ArrowLeft size={16} />
          Back
        </button>
        <div className="right-actions">
          <button className="btn-skip" onClick={onSkip}>
            Skip
            <ArrowRight size={16} />
          </button>
          <button className="btn-primary" onClick={onContinue}>
            Continue to Analysis
            <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PDFStagingModal;
